import {
  ActionType,
  formatDateToMMDDYYYY,
  Issue,
  parseMMDDYYYYToDate,
  Severity,
  useCreateMutation,
  useFindByQuery,
  useUpdateIssue,
  useUpdateMutation,
  useWorkspaceLabels
} from '@gripp/shared-logic';
import { yupResolver } from '@hookform/resolvers/yup';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Platform, ScrollView, StyleSheet, View } from 'react-native';
import { Text } from 'react-native-paper';
import { mixed, string, object as yupObject } from 'yup';
import { useAutoFocusRef } from '../../hooks';
import { Spacing } from '../../styles';
import { Colors } from '../../themes/colors';
import { Button } from '../crop-circle/Button';
import { CardButton } from '../crop-circle/CardButton';
import { TypeIcon, TypeVariant } from '../crop-circle/Components/Type';
import { colors, tokens } from '../crop-circle/tokens';
import { ControlledTextInput, DatePickerSelector, SaveFormBottom } from '../forms';
import { KeyboardSafeView } from '../keyboard/keyboardSafeView';
import { AdjustCreationDateField } from './adjustCreationDateField';
import { CreatedDateField } from './createdDateField';
import { LabelSelector } from './labelSelector';

type IssueEditModalProps = {
  issueId: string;
  currentTitle: string;
  workspaceId?: string;
  currentLabels?: Array<{ id: string; name: string }>;
  currentAdjustCreationDate?: string;
  currentCreatedAt?: string;
  issue: Issue;
  labelEditModalUrl?: string;
  bottomBar: React.ComponentType<Record<string, unknown>>;
  onReassignPress?: () => void;
  onEditCancel?: () => void;
};

type IssueEditFormData = {
  title: string;
  adjustCreationDate?: unknown;
  onReminderDate?: unknown;
};
export const IssueEdit: React.FC<IssueEditModalProps> = (props: IssueEditModalProps) => {
  const { t } = useTranslation();
  
  const filter = useMemo(() => {
    return {
      trigger: {
        type: {
          eq: 'ISSUE',
        },
      },
      and: [
        {
          disabled: {
            ne: true,
          },
        },
      ],
      json: { 'trigger.config.issueId': props.issueId, },
    };
  }, [props.issueId,]);

  const { data: workflows } = useFindByQuery({
    modelName: 'workflow',
    query: FIND_WORKFLOWS,
    variables: { filter },
  });
  
  const { mutateAsync: createWorkflow } = useCreateMutation({
    modelName: 'workflow',
    query: CREATE_WORKFLOW,
  });

  const { mutateAsync: updateWorkflow } = useUpdateMutation({
    modelName: 'workflow',
    query: UPDATE_WORKFLOW,
  });

  const { mutateAsync: updateIssue } = useUpdateIssue();
  const { mutateAsync: createLabel } = useCreateMutation({ modelName: 'label', query: CREATE_LABEL });

  const [ currentWorkflow, setCurrentWorkflow ] = useState(workflows?.items?.[0] || null); 

  // Fetch labels from current workspace
  const { labels: availableLabels } = useWorkspaceLabels(props.workspaceId);

  const formSchema = yupObject({
    title: string().required(),
    adjustCreationDate: mixed().optional(),
    onReminderDate: mixed().optional(),
  });

  const {
    control,
    handleSubmit,
    formState: { isDirty, isValid },
    reset,
    setValue,
  } = useForm({
    resolver: yupResolver(formSchema),
    mode: 'onChange',
    values: {
      title: props.currentTitle ?? '',
      adjustCreationDate: props.currentAdjustCreationDate 
        ? formatDateToMMDDYYYY(props.currentAdjustCreationDate)
        : undefined,
      onReminderDate: currentWorkflow?.trigger?.config?.onDate
        ? formatDateToMMDDYYYY(currentWorkflow.trigger.config.onDate)
        : undefined,
    },
  });

  useEffect(() => {
    const workflow = workflows?.items?.[0] || null;
    if (workflow && workflow !== currentWorkflow) {
      setCurrentWorkflow(workflow);
      if (workflow?.trigger?.config?.onDate) {
        setValue('onReminderDate', formatDateToMMDDYYYY(workflow.trigger.config.onDate));
      }
    }
  }, [workflows, currentWorkflow, setValue]);

  const onSubmit = async (data: IssueEditFormData) => {
    const input = {
      id: props.issueId,
      title: data.title,
      severity: selectedSeverity,
      labels: selectedLabels.map((l) => ({ id: l.id })),
      adjustCreationDate: data.adjustCreationDate
        ? parseMMDDYYYYToDate(String(data.adjustCreationDate))
        : undefined,
    };

    await updateIssue({ input: input });

    // Reminder logic: only if onReminderDate is not null
    if (typeof data.onReminderDate === 'string' && data.onReminderDate) {
      await createOrUpdateReminder(data.onReminderDate);
    }

    setLabelsChanged(false);
    setSeverityChanged(false);
  };

  const onEditCancel = () => {
    reset({ 
      title: props.currentTitle,
      adjustCreationDate: props.currentAdjustCreationDate 
        ? formatDateToMMDDYYYY(props.currentAdjustCreationDate)
        : undefined,
    });
    setSelectedLabels(props.currentLabels || []);
    setLabelsChanged(false);
    setSelectedSeverity(props.issue?.severity ?? Severity.Red);
    setSeverityChanged(false);

    props.onEditCancel && props.onEditCancel();
  };

  const elementFocusCallBack = useAutoFocusRef();
  const [selectedLabels, setSelectedLabels] = React.useState<Array<{ id: string; name: string }>>(props.currentLabels || []);
  const [labelsChanged, setLabelsChanged] = React.useState(false);
  const [selectedSeverity, setSelectedSeverity] = useState<Severity>(
    props.issue?.severity ?? Severity.Red
  );
  const [severityChanged, setSeverityChanged] = useState(false);

  useEffect(() => {
    setSelectedLabels(props.currentLabels || []);
    setLabelsChanged(false);
  }, [props.issueId, props.currentLabels]);
  const onReassignPress = () => {
    if (props.onReassignPress) {
      props.onReassignPress();
    }
  };

  const handleSeverityChange = (severity: Severity) => {
    setSelectedSeverity(severity);
    setSeverityChanged(severity !== (props.issue?.severity ?? Severity.Red));
  };

  const typeOptions = [
    {
      label: t('activity.filter.urgent'),
      value: Severity.Red,
      iconVariant: 'attention',
    },
    {
      label: t('activity.filter.pending'),
      value: Severity.Yellow,
      iconVariant: 'blocker',
    },
    {
      label: t('activity.filter.solved'),
      value: Severity.Green,
      iconVariant: 'solved',
    },
    {
      label: t('activity.filter.notes'),
      value: Severity.Purple,
      iconVariant: 'note',
    },
  ];

  const handleLabelsChange = (newLabels: Array<{ id: string; name: string }>) => {
    setSelectedLabels(newLabels);
    
    // Check if labels have actually changed
    const currentLabelIds = (props.currentLabels || []).map(l => l.id).sort();
    const newLabelIds = newLabels.map(l => l.id).sort();
    const hasChanged = currentLabelIds.length !== newLabelIds.length || 
                      currentLabelIds.some((id, index) => id !== newLabelIds[index]);
    
    setLabelsChanged(hasChanged);
  };

  const handleCreateLabel = async (name: string) => {
    const result = await createLabel({ input: { name, workspace: { id: props.workspaceId } } });
    return result;
  };

  const createOrUpdateReminder = async (onReminderDate: string) => {
    // This is a simplified version of the reminder creation logic from issueReminderModal.tsx
    // You may want to expand this with more fields as needed
    const workspaceId = { id: props.workspaceId };
    const trigger = {
      type: 'ISSUE',
      config: {
        issueId: props.issueId,
        onDate: parseMMDDYYYYToDate(onReminderDate),
      },
    };
    
    const action = {
      type: ActionType.Reminder,
    };

    const newReminderInput = {
      name: '',
      workspace: workspaceId,
      trigger: trigger,
      action: action,
    };

    const editReminderInput = {
      name: '',
      trigger: trigger,
      action: action,
      workspace: workspaceId,
    };
    
    if (workflows?.items?.[0]) {
      await updateReminder(editReminderInput);
    } else {
      await createReminder(newReminderInput);
    }
  };

  const createReminder = async (input: Record<string, unknown>) => {
    if (!props.issue.workspace?.id) {
      return;
    }

    const workflowInput = {
      ...input,
      workspace: {
        id: props.issue.workspace.id,
      },
    };

    await createWorkflow({ input: workflowInput });
  };

  const updateReminder = async (input: Record<string, unknown>) => {
    const updateInput = {
      ...input,
      id: workflows?.items?.[0].id,
    };

    await updateWorkflow({ input: updateInput });
  };

  // Render Revise Submission section
  const renderReviseSubmissionSection = () => {
    return null;
  };

  const onResetForm = () => {
    reset({
      title: props.currentTitle,
      adjustCreationDate: props.currentAdjustCreationDate
        ? formatDateToMMDDYYYY(props.currentAdjustCreationDate)
        : undefined,
      onReminderDate: currentWorkflow?.trigger?.config?.onDate
        ? formatDateToMMDDYYYY(currentWorkflow.trigger.config.onDate)
        : undefined,
    });
    setSelectedLabels(props.currentLabels || []);
    setLabelsChanged(false);
    setSelectedSeverity(props.issue?.severity ?? Severity.Red);
    setSeverityChanged(false);
  };

  const modalContent = (
    <>
      {renderReviseSubmissionSection()}
      <View style={styles.formItem}>
        <View>
          <Text style={styles.formItemTitle}>{t('issue.new.type')}</Text>
        </View>
        <View style={styles.typeGrid}>
          {typeOptions.map((option) => (
            <Button
              key={option.value}
              variant="rounded"
              onPress={() => handleSeverityChange(option.value)}
              leftIcon={
                <TypeIcon variant={option.iconVariant as TypeVariant} />
              }
              state={selectedSeverity === option.value ? 'active' : 'default'}
              style={{
                backgroundColor:
                  selectedSeverity !== option.value
                    ? colors.neutral[200]
                    : colors.neutral[800],
              }}
            >
              {option.label}
            </Button>
          ))}
        </View>
      </View>
      {!!props.workspaceId && (
        <View style={styles.formItem}>
          <View>
            <Text style={styles.formItemTitle}>{t('issue.new.labels')}</Text>
          </View>
          <LabelSelector
            selectedLabels={selectedLabels}
            availableLabels={availableLabels}
            onLabelsChange={handleLabelsChange}
            onCreateLabel={handleCreateLabel}
            selectorModalUrl={props.labelEditModalUrl}
          />
        </View>
      )}
      <View style={styles.formItem}>
        <View>
          <Text style={styles.formItemTitle}>{t('issue.new.reminder')}</Text>
        </View>
        <View style={[styles.inputContainer]}>
          <DatePickerSelector
            name="onReminderDate"
            setValue={setValue}
            control={control}
            placeholder="MM/DD/YYYY"
            containerStyle={{
              borderWidth: 0,
              padding: 0,
              justifyContent: 'center',
              height: '100%',
            }}
            maxYears={5}
            minDate={moment().add(1, 'day').format('YYYY-MM-DD')}
          />
        </View>
      </View>
      <View style={styles.formItem}>
        <View>
          <Text style={styles.formItemTitle}>{t('issue.new.title')}</Text>
        </View>
        <ControlledTextInput
          control={control}
          name="title"
          height={56}
          placeholder={t('issue.new.optionalPlaceholder')}
          disableInnerStyle={false}
          innerRef={elementFocusCallBack}
          backgroundColor={tokens.colors.neutral[200]}
        />
      </View>
      {
        props.issue?.asset?.name && (
          <View style={styles.formItem}>
            <View>
              <Text style={styles.formItemTitle}>{t('issue.new.reassign')}</Text>
            </View>
            <CardButton
              text={props.issue.asset.name}
              showAvatars={false}
              bordered={false}
              onPress={onReassignPress}  
            ></CardButton>
          </View>
        )
      }
      <View style={styles.formItem}>
        <AdjustCreationDateField control={control} setValue={setValue} />
      </View>
      <View style={styles.formItem}>
        <CreatedDateField 
          createdAt={props.currentCreatedAt || ''} 
          adjustCreationDate={props.currentAdjustCreationDate}
        />
      </View>
    </>
  );

  const BottomBar = props.bottomBar;

  return (
    <SaveFormBottom
      isDirty={isDirty || labelsChanged || severityChanged}
      isValid={isValid}
      onSubmit={handleSubmit(onSubmit)}
      title={t('issue.editModal.header')}
      saveLabel={t('issue.editModal.save')}
      onCancel={onEditCancel}
      onResetForm={onResetForm}
      bottomBar={(props) => <BottomBar {...props} isDirty={isDirty || labelsChanged || severityChanged} />}
    >
      {Platform.OS === 'web' ? (
        <ScrollView style={styles.formContainer}>
          {modalContent}
        </ScrollView>
      ) : (
        <KeyboardSafeView style={styles.keyboardContainer}>
          <ScrollView
            scrollEnabled
            showsVerticalScrollIndicator
          >
            {modalContent}
          </ScrollView>
        </KeyboardSafeView>
      )}
    </SaveFormBottom>
  );
};

const styles = StyleSheet.create({
  formContainer: {
    gap: 8,
    paddingBottom: Platform.OS === 'web' ? 100 : 400,
    paddingTop: 16,
    flexGrow: 1,
  },
  keyboardContainer: {
    flex: 1,
  },
  formItemTitle: {
    fontSize: 16,
    fontWeight: '700',
    lineHeight: 16,
    marginBottom: 16,
  },
  formItem: {
    paddingHorizontal: Spacing.basePadding.paddingHorizontal,
    paddingVertical: Spacing.smallPadding.paddingVertical,
    backgroundColor: Colors.white,
    marginBottom: 4,
  },
  typeGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  mobileContainer: {
    backgroundColor: Colors.primaryGrayBackground,
    height: '100%',
  },
  reviseSubmissionItem: {
    backgroundColor: Colors.blue,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: Colors.graySpacer,
  },
  reviseSubmissionContent: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
  },
  reviseSubmissionIcon: {
    marginRight: 12,
  },
  reviseSubmissionText: {
    flex: 1,
  },
  reviseSubmissionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: Colors.white,
  },
  inputRow: {
    flexDirection: 'row',
    ...Platform.select({
      web: {
        width: '100%',
        minWidth: '100%',
        maxWidth: '100%',
      },
      ios: {},
      android: {},
    }),
  },
  inputContainer: {
    flex: 1,
    borderRadius: 8,
    flexDirection: 'row',
    alignItems: 'center',
    ...Platform.select({
      web: {
        width: '100%',
        minWidth: '100%',
        maxWidth: '100%',
      },
      ios: {},
      android: {},
    }),
  },
});

const CREATE_LABEL = `
  mutation CreateLabel($input: CreateLabelInput!) {
    createLabel(input: $input) { id name }
  }
`;

const CREATE_WORKFLOW = `mutation createWorkflow($input: CreateWorkflowInput!) {
  createWorkflow(input: $input) {
    id
  }
}`;

const UPDATE_WORKFLOW = `
  mutation UpdateWorkflow($input: MutateWorkflowInput!) {
  updateWorkflow(input: $input) {
    id
  }
}`;

const FIND_WORKFLOWS = `
  query FindWorkflows($filter: WorkflowFilter) {
    findWorkflows(filter: $filter) {
      count
      items {
        id
        name
        workspace {
          id
        }
        trigger {
          type
          config
        }
        action {
          type
          config
        }
      }
    }
  }
`;

